import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiErrorDetail, formatINR, formatDate, formatDateTime, API } from "@/lib/api";
import {
  LayoutDashboard, Users, ListChecks, Calendar, Award, HandCoins,
  Loader2, Plus, X, Download, Settings,
} from "lucide-react";
import { Link, Routes, Route } from "react-router-dom";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import useAutoRefresh from "@/hooks/useAutoRefresh";

import DonationsTab from "@/components/admin/DonationsTab";
import EmployeesTab from "@/components/admin/EmployeesTab";
import AttendanceTab from "@/components/admin/AttendanceTab";
import SheetTab from "@/components/admin/SheetTab";
import SettingsTab from "@/components/admin/SettingsTab";
import IncentivesTab from "@/components/admin/IncentivesTab";
import PayrollTab from "@/components/admin/PayrollTab";
import ClashResolutionTab from "@/components/admin/ClashResolutionTab";
import OfficeClosureTab from "@/components/admin/OfficeClosureTab";
import ExportTab from "@/components/admin/ExportTab";

const NAV = [
  { key: "overview", to: "/admin", label: "Overview", icon: LayoutDashboard, group: "Workspace" },
  { key: "donations", to: "/admin/donations", label: "Donations", icon: ListChecks, group: "Workspace" },
  { key: "clashes", to: "/admin/clashes", label: "Clashes", icon: ListChecks, group: "Workspace" },
  { key: "donors", to: "/admin/donors", label: "Donors", icon: HandCoins, group: "Workspace" },
  { key: "rechurn", to: "/admin/rechurn", label: "Rechurn", icon: HandCoins, group: "Workspace" },
  { key: "employees", to: "/admin/employees", label: "Employees", icon: Users, group: "Team & pay" },
  { key: "attendance", to: "/admin/attendance", label: "Attendance", icon: Calendar, group: "Team & pay" },
  { key: "leaves", to: "/admin/leaves", label: "Leaves", icon: Calendar, group: "Team & pay" },
  { key: "incentives", to: "/admin/incentives", label: "Incentives", icon: Award, group: "Team & pay" },
  { key: "payroll", to: "/admin/payroll", label: "Payroll", icon: HandCoins, group: "Team & pay" },
  { key: "export", to: "/admin/export", label: "Export", icon: Download, group: "System" },
  { key: "sheet", to: "/admin/sheet", label: "Sheet Sync", icon: HandCoins, group: "System" },
  { key: "office-closure", to: "/admin/office-closure", label: "Office Closure", icon: Calendar, group: "System" },
  { key: "settings", to: "/admin/settings", label: "Settings", icon: Settings, group: "System" },
];

export default function AdminDashboard() {
  return (
    <AppShell title="Admin Console" subtitle="Full visibility over donations, employees & incentives." nav={NAV}>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="donations" element={<DonationsTab />} />
        <Route path="clashes" element={<ClashResolutionTab />} />
        <Route path="employees" element={<EmployeesTab />} />
        <Route path="attendance" element={<AttendanceTab />} />
        <Route path="leaves" element={<LeavesTab />} />
        <Route path="incentives" element={<IncentivesTab />} />
        <Route path="payroll" element={<PayrollTab />} />
        <Route path="export" element={<ExportTab />} />
        <Route path="donors" element={<DonorsTab />} />
        <Route path="rechurn" element={<RechurnTab />} />
        <Route path="sheet" element={<SheetTab />} />
        <Route path="office-closure" element={<OfficeClosureTab />} />
        <Route path="settings" element={<SettingsTab />} />
      </Routes>
    </AppShell>
  );
}

/* ------------ Overview ------------ */
function Overview() {
  const [stats, setStats] = useState(null);
  const [exceptions, setExceptions] = useState(null);
  const [callerPeriod, setCallerPeriod] = useState("month");
  const [callerSort, setCallerSort] = useState("amount");
  const [breakdownDate, setBreakdownDate] = useState("");
  const loadStats = () => Promise.all([
    api.get("/admin/dashboard-stats").then((r) => setStats(r.data)),
    api.get("/admin/dashboard-exceptions").then((r) => setExceptions(r.data)),
  ]).catch(() => {});
  useEffect(() => { loadStats(); }, []);
  useAutoRefresh(loadStats, [], { intervalMs: 20000 });
  useEffect(() => {
    if (stats?.today_attendance_date && !breakdownDate) setBreakdownDate(stats.today_attendance_date);
  }, [stats, breakdownDate]);

  if (!stats) return <div className="text-[#5C635E]"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const topCallers = [...(stats[`top_callers_${callerPeriod}`] || [])].sort((a, b) => {
    if (callerSort === "count") return (b.count || 0) - (a.count || 0) || (b.amount || 0) - (a.amount || 0);
    return (b.amount || 0) - (a.amount || 0) || (b.count || 0) - (a.count || 0);
  });
  const breakdownDays = stats.per_day_last_90 || stats.per_day_last_14 || [];
  const selectedBreakdown = breakdownDays.find((row) => row.date === breakdownDate)
    || (!breakdownDate ? breakdownDays.slice(-1)[0] : null)
    || { date: breakdownDate, team_total: 0, callers: [] };

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Total Raised" value={formatINR(stats.total_amount)} accent />
        <Kpi label="This Month" value={formatINR(stats.monthly_amount)} />
        <Kpi label="Today" value={formatINR(stats.daily_amount)} />
        <Kpi label="Unique Donors" value={String(stats.total_donors)} sub={`${stats.active_employees} active employees`} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="admin-alerts">
        <Kpi label="Pending Approvals" value={String(stats.pending_approvals || 0)} sub="Donations awaiting review" />
        <Kpi label="Pending Leaves" value={String(stats.pending_leaves || 0)} sub="Requests to action" />
        <Kpi label="Donor Clashes" value={String(stats.clash_count || 0)} sub="Flagged for verification" />
      </div>

      {exceptions && <div className="card-warm p-4" data-testid="admin-exception-dashboard">
        <div className="text-overline mb-3">Needs attention</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <ExceptionLink label="Pending" value={exceptions.pending_donations} to="/admin/donations" />
          <ExceptionLink label="Clashes" value={exceptions.clashes} to="/admin/clashes" />
          <ExceptionLink label="Unexported" value={exceptions.unexported} to="/admin/settings" />
          <ExceptionLink label="Missing attendance" value={exceptions.missing_attendance} to="/admin/attendance" />
        </div>
      </div>}

      <div className="card-warm p-4" data-testid="admin-quick-actions">
        <div className="text-overline mb-3">Quick actions</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {[
            ["Review donations", "/admin/donations", ListChecks],
            ["Check attendance", "/admin/attendance", Calendar],
            ["Manage employees", "/admin/employees", Users],
            ["Review payroll", "/admin/payroll", HandCoins],
          ].map(([label, to, Icon]) => <Link key={to} to={to} className="flex items-center gap-2 rounded-xl border border-[#EAE6DF] bg-[#FDFBF7] px-3 py-3 text-sm text-[#2D312E] hover:border-[#4A5D4E] hover:bg-white transition-colors"><Icon className="h-4 w-4 text-[#4A5D4E]" /><span>{label}</span></Link>)}
        </div>
      </div>

      <div className="hidden" aria-hidden="true" data-testid="admin-today-attendance">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-overline">Today · {stats.today_attendance_date}</div>
            <h3 className="text-xl font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Attendance status</h3>
          </div>
          <div className="flex gap-2 text-xs">
            <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              Present {stats.today_attendance_present || 0}
            </span>
            <span className="px-3 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
              Absent {stats.today_attendance_absent || 0}
            </span>
          </div>
        </div>
        <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full min-w-[780px] text-sm">
            <thead>
              <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
                <th className="text-left p-3">Employee</th>
                <th className="text-left p-3">Present / Absent</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Clock in</th>
                <th className="text-left p-3">Clock out</th>
                <th className="text-left p-3">Office</th>
              </tr>
            </thead>
            <tbody>
              {(stats.today_attendance || []).length === 0 ? (
                <tr><td colSpan="6" className="p-6 text-center text-[#8A938D]">No active employees.</td></tr>
              ) : (stats.today_attendance || []).map((a) => (
                <tr key={a.employee_id} className="border-t border-[#EAE6DF]">
                  <td className="p-3 font-medium text-[#2D312E]">{a.employee_name}</td>
                  <td className="p-3">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium border ${a.present_absent === "present" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                      {a.present_absent === "present" ? "Present" : "Absent"}
                    </span>
                  </td>
                  <td className="p-3 text-[#5C635E] capitalize">{String(a.status || "absent").replace("_", " ")}</td>
                  <td className="p-3 text-[#5C635E]">{a.login_time ? formatDateTime(a.login_time) : "—"}</td>
                  <td className="p-3 text-[#5C635E]">{a.logout_time ? formatDateTime(a.logout_time) : "—"}</td>
                  <td className="p-3 text-[#5C635E]">{a.in_office === true ? "In office" : a.in_office === false ? "Out of office" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="hidden" aria-hidden="true" data-testid="admin-today-incentives">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-overline">Today</div>
            <h3 className="text-xl font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Available incentives</h3>
          </div>
        </div>
        {(stats.today_incentives || []).length === 0 ? (
          <div className="text-sm text-[#8A938D]">No active incentive slabs for today.</div>
        ) : (
          <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
                  <th className="text-left p-3">Employee</th>
                  <th className="text-left p-3">Daily</th>
                  <th className="text-left p-3">Weekly</th>
                  <th className="text-left p-3">Monthly</th>
                  <th className="text-left p-3">Hourly</th>
                </tr>
              </thead>
              <tbody>
                {(stats.today_incentives || []).map((row) => (
                  <tr key={row.employee_id} className="border-t border-[#EAE6DF]">
                    <td className="p-3 font-medium text-[#2D312E]">{row.employee_name}</td>
                    <td className="p-3 text-xs text-[#5C635E]">{formatTierList(row.daily_tiers, "collection", row.rule_incentives, "daily")}</td>
                    <td className="p-3 text-xs text-[#5C635E]">{formatWeeklyTierList(row)}</td>
                    <td className="p-3 text-xs text-[#5C635E]">{formatTierList(row.monthly_tiers, "month", row.rule_incentives, "monthly")}</td>
                    <td className="p-3 text-xs text-[#5C635E]">{formatHourlyRules(row.hourly_rules, row.rule_incentives)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="hidden" data-testid="admin-per-day">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-overline mb-2">{selectedBreakdown.date ? formatDate(selectedBreakdown.date) : "Select day"}</div>
            <h3 className="text-xl font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Team & caller breakdown</h3>
          </div>
          <input
            data-testid="team-breakdown-date"
            type="date"
            value={breakdownDate || selectedBreakdown.date || ""}
            onChange={(e) => setBreakdownDate(e.target.value)}
            className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] text-sm">
            <thead><tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
              <th className="text-left p-3">Employee</th><th className="text-right p-3">Donations</th><th className="text-right p-3">Amount</th>
            </tr></thead>
            <tbody>
              {(selectedBreakdown.callers || []).length === 0 ? (
                <tr><td colSpan="3" className="p-6 text-center text-[#8A938D]">No donations for this day.</td></tr>
              ) : (selectedBreakdown.callers || []).map((caller) => (
                <tr key={caller.employee_id || caller.employee_name} className="border-t border-[#EAE6DF]">
                  <td className="p-3 font-medium text-[#2D312E]">{caller.employee_name || "Unknown"}</td>
                  <td className="p-3 text-right text-[#5C635E]">{caller.count || 0}</td>
                  <td className="p-3 text-right font-semibold text-[#4A5D4E]">{formatINR(caller.amount || 0)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-[#EAE6DF] bg-[#FDFBF7]">
                <td className="p-3 font-semibold text-[#2D312E]">Team total</td>
                <td className="p-3"></td>
                <td className="p-3 text-right font-semibold text-[#4A5D4E]">{formatINR(selectedBreakdown.team_total || 0)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <div className="card-warm p-6" data-testid="admin-chart-trend">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-overline">Trend</div>
            <h3 className="text-xl font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Last 30 days</h3>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={stats.trend_last_30_days}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8A938D" }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: "#8A938D" }} tickFormatter={(v) => v >= 1000 ? `${v/1000}k` : v} />
              <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ background: "#fff", border: "1px solid #EAE6DF", borderRadius: 12 }} />
              <Line type="monotone" dataKey="amount" stroke="#4A5D4E" strokeWidth={2.5} dot={{ r: 3, fill: "#4A5D4E" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card-warm p-6" data-testid="admin-top-callers">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-overline mb-2">{callerPeriod === "day" ? "Today" : callerPeriod === "week" ? "Last 7 days" : "This month"}</div>
            <h3 className="text-xl font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Employee donations</h3>
          </div>
          <div className="flex gap-2 text-sm flex-wrap" data-testid="top-callers-period">
            {["day", "week", "month"].map((p) => (
              <button key={p} type="button" data-testid={`top-callers-${p}`} onClick={() => setCallerPeriod(p)}
                className={`px-3 py-1.5 rounded-lg capitalize ${callerPeriod === p ? "bg-[#4A5D4E] text-white" : "border border-[#EAE6DF] text-[#5C635E]"}`}>
                {p}
              </button>
            ))}
            <select
              data-testid="employee-donations-sort"
              value={callerSort}
              onChange={(e) => setCallerSort(e.target.value)}
              className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-1.5"
            >
              <option value="amount">Sort by amount</option>
              <option value="count">Sort by donations</option>
            </select>
          </div>
        </div>
        {topCallers.length === 0 ? (
          <div className="text-sm text-[#8A938D]">No employees found for this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm">
              <thead>
                <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
                  <th className="text-left p-3">Employee</th>
                  <th className="text-right p-3">Donations</th>
                  <th className="text-right p-3">Amount</th>
                </tr>
              </thead>
              <tbody>
                {topCallers.map((c) => (
                  <tr key={c.employee_id || c.employee_name} className="border-t border-[#EAE6DF]">
                    <td className="p-3 font-medium text-[#2D312E]">{c.employee_name || "Unknown"}</td>
                    <td className="p-3 text-right text-[#5C635E]">{c.count}</td>
                    <td className="p-3 text-right font-semibold text-[#4A5D4E]">{formatINR(c.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card-warm p-6" data-testid="admin-top-employees">
        <div className="text-overline mb-2">This month</div>
        <h3 className="text-xl font-medium text-[#2D312E] mb-4" style={{ fontFamily: "Manrope" }}>Top employees</h3>
        {stats.top_employees_month.length === 0 ? (
          <div className="text-sm text-[#8A938D]">No donations yet this month.</div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.top_employees_month} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#8A938D" }} tickFormatter={(v) => v >= 1000 ? `${v/1000}k` : v} />
                <YAxis dataKey="employee_name" type="category" tick={{ fontSize: 12, fill: "#5C635E" }} width={110} />
                <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ background: "#fff", border: "1px solid #EAE6DF", borderRadius: 12 }} />
                <Bar dataKey="amount" fill="#C05746" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function ExceptionLink({ label, value, to, danger }) {
  return <Link to={to} className={`rounded-xl border p-3 transition-colors ${danger ? "border-red-200 bg-red-50" : "border-[#EAE6DF] bg-[#FDFBF7] hover:border-[#4A5D4E]"}`}>
    <div className="text-[10px] uppercase tracking-wider text-[#8A938D]">{label}</div>
    <div className={`mt-1 text-xl font-semibold ${danger ? "text-red-700" : "text-[#2D312E]"}`}>{value ?? 0}</div>
  </Link>;
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div className={`card-warm card-warm-hover p-6 ${accent ? "bg-[#4A5D4E] text-white" : ""}`}>
      <div className={`text-overline ${accent ? "!text-white/70" : ""}`}>{label}</div>
      <div className={`text-3xl mt-3 ${accent ? "text-white" : "text-[#2D312E]"}`} style={{ fontFamily: "Manrope" }}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${accent ? "text-white/70" : "text-[#8A938D]"}`}>{sub}</div>}
    </div>
  );
}

function formatTierList(tiers = [], basis = "collection", standalone = [], type = "") {
  const lines = [
    ...(tiers || []).map((tier) => formatIncentiveTier(tier, basis)),
    ...formatStandaloneRules(standalone, type, basis),
  ];
  return lines.length ? lines.join(" | ") : "Not active";
}

function formatWeeklyTierList(row) {
  const rules = formatTierList(row.weekly_tiers, "week", row.rule_incentives, "weekly");
  if (rules === "Not active") return rules;
  const range = formatWeeklyRange(row);
  return range ? `${range} | ${rules}` : rules;
}

function formatHourlyRules(rules = [], standalone = []) {
  const lines = [
    ...rules.flatMap((rule) => (rule.slabs || [])
      .map((tier) => `${rule.start_time}-${rule.end_time}: ${formatIncentiveTier(tier, "donation").replace("If", "if")}`)),
    ...formatStandaloneRules(standalone, "hourly", "donation"),
  ];
  return lines.length ? lines.join(" | ") : "Not active";
}

function formatStandaloneRules(rules = [], type = "", basis = "collection") {
  return (rules || [])
    .filter((rule) => rule.type === type)
    .flatMap((rule) => (rule.tiers || []).map((tier) => {
      const prefix = rule.backdated_only ? "Backdated only: " : "";
      return `${prefix}${rule.name || "Incentive"} (${formatRulePeriod(rule)}): ${formatIncentiveTier(tier, basis).replace(/^Backdated only: /, "").replace(/^If /, "if ")}`;
    }));
}

function formatRulePeriod(rule) {
  if (rule.type === "monthly") return rule.month || "";
  if (rule.type === "weekly") return `${formatDate(rule.start_date)} to ${formatDate(rule.end_date)}`;
  if (rule.type === "hourly") return `${formatDate(rule.date)} ${rule.start_time || ""}-${rule.end_time || ""}`;
  return formatDate(rule.date);
}

function formatIncentiveTier(tier, basis) {
  const payout = tier.unit === "percent" ? `${tier.amount}% of slab amount` : formatINR(tier.amount);
  const prefix = tier.backdated_only ? "Backdated only: " : "";
  if (tier.basis === "target_percent") {
    return `${prefix}If reaches ${tier.min_target_percent ?? tier.min_amount ?? 0}% of monthly target, get ${payout}`;
  }
  return `${prefix}If ${basis} reaches ${formatINR(tier.min_amount)}, get ${payout}`;
}

function formatWeeklyRange(row) {
  if (!row?.weekly_start_date && !row?.weekly_end_date) return "";
  if (row?.weekly_start_date && row?.weekly_end_date) {
    return `${formatDate(row.weekly_start_date)} to ${formatDate(row.weekly_end_date)}`;
  }
  return row?.weekly_start_date ? `From ${formatDate(row.weekly_start_date)}` : `Until ${formatDate(row.weekly_end_date)}`;
}


/* ------------ Leaves ------------ */
function LeavesTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/leaves").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, [], { intervalMs: 20000 });
  const decide = async (id, status) => {
    try { await api.patch(`/admin/leaves/${id}`, { status }); toast.success(status); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-4" data-testid="admin-leaves">
      <div>
        <div className="text-overline">Requests</div>
        <h2 className="text-2xl font-light text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Leave requests</h2>
      </div>
      <div className="card-warm overflow-hidden">
        <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
        <table className="w-full min-w-[860px] text-sm">
          <thead>
            <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
              <th className="text-left p-3">Employee</th><th className="text-left p-3">Date</th><th className="text-left p-3">Type</th><th className="text-left p-3">Reason</th><th className="text-left p-3">Status</th><th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (<tr><td colSpan="6" className="p-8 text-center text-[#8A938D]">No leave requests.</td></tr>) : items.map((l) => (
              <tr key={l.id} data-testid={`leave-row-${l.id}`} className="border-t border-[#EAE6DF]">
                <td className="p-3 font-medium text-[#2D312E]">{l.employee_name}</td>
                <td className="p-3 text-[#5C635E]">{formatDate(l.date)}</td>
                <td className="p-3 text-[#5C635E]">{l.leave_type === "half_day" ? "Half day" : "Full day"}</td>
                <td className="p-3 text-[#5C635E]">{l.reason}</td>
                <td className="p-3"><span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${l.status==="approved"?"bg-emerald-50 text-emerald-700":l.status==="rejected"?"bg-red-50 text-red-700":"bg-amber-50 text-amber-700"}`}>{l.status}</span></td>
                <td className="p-3 text-right">
                  {l.status === "pending" && (
                    <>
                      <button data-testid={`approve-leave-${l.id}`} onClick={() => decide(l.id, "approved")} className="text-emerald-700 hover:underline text-sm mr-3">Approve</button>
                      <button data-testid={`reject-leave-${l.id}`} onClick={() => decide(l.id, "rejected")} className="text-[#C05746] hover:underline text-sm">Reject</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

/* ------------ Donors ------------ */
function DonorsTab() {
  const [items, setItems] = useState([]);
  const [org, setOrg] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const donorQuery = () => {
    const params = new URLSearchParams();
    if (org) params.set("organization", org);
    if (search.trim()) params.set("search", search.trim());
    params.set("sort_by", sortBy);
    params.set("sort_dir", sortDir);
    return params.toString();
  };
  const load = () => api.get(`/admin/donors?${donorQuery()}`).then((r) => setItems(r.data));
  // `load` intentionally follows the filter values that form its query.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [org, search, sortBy, sortDir]);

  const exportCsv = () => {
    const url = `${API}/admin/export/donors?${donorQuery()}`;
    fetch(url, { credentials: "include" })
      .then(r => r.blob()).then(blob => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "donors.csv"; a.click(); });
  };

  const total = items.reduce((s,x)=>s+x.total_amount,0);

  return (
    <div className="space-y-4" data-testid="admin-donors">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-overline">Community</div>
          <h2 className="text-2xl font-light text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Donor history ({items.length})</h2>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <input data-testid="donors-search" value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search name, phone, caller"
            className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm min-w-[220px]" />
          <select data-testid="donors-org-filter" value={org} onChange={e=>setOrg(e.target.value)} className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm uppercase">
            <option value="">All organizations</option>
            {["ccm","ketto","sbf","bof"].map(o=><option key={o} value={o}>{o}</option>)}
          </select>
          <select data-testid="donors-sort-by" value={sortBy} onChange={e=>setSortBy(e.target.value)} className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm">
            <option value="total_amount">Total amount</option>
            <option value="donation_count">Donation count</option>
            <option value="last_donation">Last donation</option>
            <option value="first_donation">First donation</option>
            <option value="name">Name</option>
            <option value="phone">Phone</option>
            <option value="employee_name">Caller</option>
            <option value="organization">Organization</option>
          </select>
          <select data-testid="donors-sort-dir" value={sortDir} onChange={e=>setSortDir(e.target.value)} className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm">
            <option value="desc">High to low</option>
            <option value="asc">Low to high</option>
          </select>
          <button data-testid="export-donors-csv" onClick={exportCsv} className="border border-[#EAE6DF] hover:border-[#4A5D4E] rounded-lg px-4 py-2 text-sm text-[#2D312E] flex items-center gap-2"><Download className="h-4 w-4" />Export CSV</button>
        </div>
      </div>
      <div className="card-warm p-3 text-sm text-[#5C635E]">Team total from these donors: <span className="font-semibold text-[#2D312E]">{formatINR(total)}</span></div>
      <div className="card-warm overflow-hidden">
        <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
        <table className="w-full min-w-[860px] text-sm">
          <thead>
            <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
              <th className="text-left p-3">Donor</th><th className="text-left p-3">Phone</th><th className="text-left p-3">Org</th><th className="text-left p-3">Caller</th>
              <th className="text-right p-3">Donations</th><th className="text-right p-3">Total</th><th className="text-left p-3">Last</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (<tr><td colSpan="7" className="p-8 text-center text-[#8A938D]">No donors.</td></tr>) : items.map((d) => (
              <tr key={d.phone} data-testid={`donor-row-${d.phone}`} className={`border-t border-[#EAE6DF] ${d.stale ? "bg-red-50/40" : ""}`}>
                <td className={`p-3 font-medium ${d.stale?"text-[#C05746]":"text-[#2D312E]"}`}>{d.name}{d.stale && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300">Stale 3m+</span>}</td>
                <td className="p-3 text-[#5C635E]">{d.phone}</td>
                <td className="p-3 uppercase text-[#5C635E]">{d.organization || "—"}</td>
                <td className="p-3 text-[#5C635E]">{d.employee_name || "—"}</td>
                <td className="p-3 text-right text-[#5C635E]">{d.donation_count}</td>
                <td className="p-3 text-right font-semibold text-[#4A5D4E]">{formatINR(d.total_amount)}</td>
                <td className="p-3 text-[#5C635E]">{formatDate(d.last_donation)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

/* ------------ Rechurn (admin) ------------ */
function RechurnTab() {
  const [items, setItems] = useState([]);
  const [empFilter, setEmpFilter] = useState("");
  const [employees, setEmployees] = useState([]);

  const load = () => {
    const q = empFilter ? `?employee_id=${empFilter}` : "";
    api.get(`/admin/rechurn${q}`).then(r => setItems(r.data));
  };
  useEffect(() => { api.get("/admin/employees").then(r => setEmployees(r.data)); }, []);
  // `load` intentionally follows the selected employee filter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [empFilter]);

  return (
    <div className="space-y-4" data-testid="admin-rechurn">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-overline">Reactivation</div>
          <h2 className="text-2xl font-light text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Rechurn — donors quiet 3+ months ({items.length})</h2>
        </div>
        <select data-testid="rechurn-emp-filter" value={empFilter} onChange={e => setEmpFilter(e.target.value)}
          className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm">
          <option value="">All callers</option>
          {[...employees].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </div>
      <div className="card-warm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
            <th className="text-left p-3">Donor</th><th className="text-left p-3">Phone</th>
            <th className="text-left p-3">Assigned Caller</th><th className="text-left p-3">Org</th>
            <th className="text-right p-3">Total</th><th className="text-right p-3">Donations</th>
            <th className="text-left p-3">Last donation</th>
          </tr></thead>
          <tbody>
            {items.length === 0 ? (<tr><td colSpan="7" className="p-8 text-center text-[#8A938D]">No stale donors.</td></tr>) : items.map((d) => (
              <tr key={d.phone} data-testid={`rechurn-row-${d.phone}`} className="border-t border-[#EAE6DF] bg-red-50/40">
                <td className="p-3 font-medium text-[#C05746]">{d.name}</td>
                <td className="p-3 text-[#5C635E]">{d.phone}</td>
                <td className="p-3 text-[#2D312E]">{d.employee_name || "—"}</td>
                <td className="p-3 uppercase text-[#5C635E]">{d.organization || "—"}</td>
                <td className="p-3 text-right font-semibold text-[#4A5D4E]">{formatINR(d.total_amount)}</td>
                <td className="p-3 text-right text-[#5C635E]">{d.donation_count}</td>
                <td className="p-3 text-[#5C635E]">{formatDate(d.last_donation)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
